import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { AlertCircleIcon, ChevronDown, X } from "lucide-react-native";
import { useEffect, useState } from "react";
import {
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { Badge, BadgeText } from "../../ui/badge";
import { Box } from "../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Input, InputField, InputIcon, InputSlot } from "../../ui/input";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "../../ui/popover";
import { Spinner } from "../../ui/spinner";
import { Text } from "../../ui/text";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

type Props<TFieldValues extends FieldValues> = FormInputSelectProps<
  TFieldValues,
  string[]
> & {
  loading?: boolean;
  emptyOptionsMessage?: string;
  getValues?: UseFormGetValues<TFieldValues>;
  setValue?: UseFormSetValue<TFieldValues>;
};

export default function FormInputMultipleMenu<TFieldValues extends FieldValues>(
  props: Props<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    placeholder,
    loading,
    emptyOptionsMessage,
    getValues,
    setValue,
  } = props;
  const [search, setSearch] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (!getValues || !setValue) return;
    const values = getValues(name);
    const filteredValues = values.filter((v: string) =>
      options.some((o) => o.value === v),
    );
    setValue(name, filteredValues);
  }, [options, getValues, setValue, name]);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        const unSelectedOptions = options.filter(
          (option) => !value?.includes(option.value),
        );

        const filteredOptions = unSelectedOptions.filter((option) =>
          option.label.toLowerCase().includes(search.toLowerCase()),
        );

        const selectedOptions = options.filter((option) =>
          value.includes(option.value),
        );

        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
          >
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
            {selectedOptions.length > 0 && (
              <HStack space="sm" className="flex-wrap mt-1">
                {selectedOptions.map((option) => {
                  return (
                    <Badge
                      action="muted"
                      variant="outline"
                      rounded="2xl"
                      size="md"
                      key={option.value}
                    >
                      <BadgeText>{option.label}</BadgeText>
                      <Button
                        variant="link"
                        size="none"
                        className="ml-1"
                        onPress={() => {
                          onChange(
                            value.filter((v: string) => v !== option.value),
                          );
                          setIsOpen(false);
                        }}
                      >
                        <ButtonIcon size="sm" as={X} />
                      </Button>
                    </Badge>
                  );
                })}
              </HStack>
            )}
            <Box ref={focusScrollRef(ref)}>
              <Popover
                placement="bottom left"
                offset={5}
                isOpen={isOpen}
                trapFocus={false}
                focusScope={false}
                size="lg"
                onClose={() => setIsOpen(false)}
                trigger={({ ...triggerProps }) => {
                  return (
                    <Input
                      className="my-1 pr-2"
                      isDisabled={disabled}
                      isInvalid={!!error}
                      size={size}
                    >
                      <InputField
                        ref={triggerProps.ref}
                        placeholder={placeholder}
                        value={search}
                        onFocus={() => {
                          setIsOpen(true);
                          setTimeout(() => {
                            triggerProps.ref.current?.focus();
                          }, 50);
                        }}
                        onChangeText={setSearch}
                        autoFocus={isOpen}
                      />
                      <InputSlot>
                        {loading ? (
                          <Spinner size="small" />
                        ) : (
                          <InputIcon as={ChevronDown} />
                        )}
                      </InputSlot>
                    </Input>
                  );
                }}
              >
                <PopoverBackdrop />
                <PopoverContent className="p-1 min-w-[400px] flex-1">
                  <PopoverBody className="">
                    {filteredOptions.map((content) => {
                      return (
                        <Button
                          className="rounded-lg justify-start"
                          variant="solid"
                          action="default"
                          size="sm"
                          onPress={() => {
                            onChange([...(value || []), content.value]);
                            setSearch("");
                          }}
                          key={`${content.value}-item`}
                        >
                          <ButtonText size={size}>{content.label}</ButtonText>
                        </Button>
                      );
                    })}
                    {!filteredOptions.length && emptyOptionsMessage && (
                      <Box className="px-4 py-4">
                        <Text action="muted" size="sm">
                          {emptyOptionsMessage}
                        </Text>
                      </Box>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
